import type { ChangeEvent, FC, FormEvent, ReactNode } from 'react'

import { useState } from 'react'
import { Check, Loader2 } from 'lucide-react'

import { Seo } from '@/components/generals/seo'
import { Navbar } from '@/components/generals/navbar'

type Status = 'idle' | 'submitting' | 'success' | 'error'

interface FormState {
    name: string
    email: string
    subject: string
    message: string
}

const EMPTY_FORM: FormState = { name: '', email: '', subject: '', message: '' }

const Contact: FC = (): ReactNode => {
    const [form, setForm] = useState<FormState>(EMPTY_FORM)
    const [status, setStatus] = useState<Status>('idle')
    const [error, setError] = useState<string>('')

    const update = (field: keyof FormState) => (
        e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        setForm((prev) => ({ ...prev, [field]: e.target.value }))
    }

    const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault()
        if (status === 'submitting') return

        setStatus('submitting')
        setError('')

        try {
            const res = await fetch('/api/contact', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(form)
            })
            const data = await res.json()

            if (!res.ok || !data.ok) {
                setError(data.error || 'Something went wrong. Please try again.')
                setStatus('error')
                return
            }

            setForm(EMPTY_FORM)
            setStatus('success')
        } catch {
            setError('Could not reach the server. Please check your connection and try again.')
            setStatus('error')
        }
    }

    const inputClass =
        'w-full rounded-lg border border-border bg-card px-4 py-3 text-[15px] text-foreground placeholder:text-muted-foreground/70 outline-none transition-colors focus:border-foreground/40 focus:ring-2 focus:ring-ring/30'
    const labelClass = 'mb-2 block text-[11px] uppercase tracking-[0.18em] text-muted-foreground'

    return (
        <>
            <Seo
                title="Contact"
                description="Get in touch about prints, licensing, or commissions from the Moments gallery."
            />

            <Navbar />

            <main className="min-h-screen bg-background px-5 pb-24 pt-32 sm:px-7">
                <div className="mx-auto max-w-xl">
                    <p className="flex items-center gap-3 text-[11px] uppercase tracking-[0.22em] text-muted-foreground">
                        <span className="inline-block h-px w-8 bg-border" />
                        Contact
                    </p>

                    <h1
                        className="mt-5 text-foreground"
                        style={{
                            fontFamily: 'var(--font-playfair), Georgia, serif',
                            fontSize: 'clamp(2.25rem, 5vw, 3.5rem)',
                            lineHeight: 1,
                            letterSpacing: '-0.02em'
                        }}
                    >
                        Say hello
                    </h1>

                    <p className="mt-5 text-[15px] leading-relaxed text-muted-foreground">
                        Questions about prints, licensing, or a commission? Send a note and we will get back to you.
                    </p>

                    {status === 'success' ? (
                        <div
                            role="status"
                            className="mt-10 flex items-start gap-4 rounded-xl border border-border bg-card p-6"
                        >
                            <span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-foreground text-background">
                                <Check className="size-4" />
                            </span>
                            <div>
                                <p className="text-[15px] font-medium text-foreground">Message sent</p>
                                <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
                                    Thanks for reaching out. Your message is saved and we will reply to your email soon.
                                </p>
                                <button
                                    type="button"
                                    onClick={() => setStatus('idle')}
                                    className="mt-4 text-[11px] uppercase tracking-[0.2em] text-foreground underline-offset-4 hover:underline"
                                >
                                    Send another
                                </button>
                            </div>
                        </div>
                    ) : (
                        <form onSubmit={onSubmit} noValidate className="mt-10 space-y-5">
                            <div>
                                <label htmlFor="name" className={labelClass}>
                                    Name
                                </label>
                                <input
                                    id="name"
                                    name="name"
                                    type="text"
                                    autoComplete="name"
                                    required
                                    value={form.name}
                                    onChange={update('name')}
                                    className={inputClass}
                                    placeholder="Your name"
                                />
                            </div>

                            <div>
                                <label htmlFor="email" className={labelClass}>
                                    Email
                                </label>
                                <input
                                    id="email"
                                    name="email"
                                    type="email"
                                    autoComplete="email"
                                    required
                                    value={form.email}
                                    onChange={update('email')}
                                    className={inputClass}
                                    placeholder="you@example.com"
                                />
                            </div>

                            <div>
                                <label htmlFor="subject" className={labelClass}>
                                    Subject <span className="normal-case tracking-normal">(optional)</span>
                                </label>
                                <input
                                    id="subject"
                                    name="subject"
                                    type="text"
                                    value={form.subject}
                                    onChange={update('subject')}
                                    className={inputClass}
                                    placeholder="What is this about?"
                                />
                            </div>

                            <div>
                                <label htmlFor="message" className={labelClass}>
                                    Message
                                </label>
                                <textarea
                                    id="message"
                                    name="message"
                                    required
                                    rows={5}
                                    value={form.message}
                                    onChange={update('message')}
                                    className={`${inputClass} resize-y`}
                                    placeholder="Tell us a little more..."
                                />
                            </div>

                            {status === 'error' && (
                                <p role="alert" className="text-sm text-destructive">
                                    {error}
                                </p>
                            )}

                            <button
                                type="submit"
                                disabled={status === 'submitting'}
                                className="inline-flex items-center justify-center gap-2 rounded-full bg-foreground px-7 py-3 text-sm font-medium text-background transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-70"
                            >
                                {status === 'submitting' && <Loader2 className="size-4 animate-spin" />}
                                {status === 'submitting' ? 'Sending...' : 'Send message'}
                            </button>
                        </form>
                    )}
                </div>
            </main>
        </>
    )
}

export default Contact
